/**
 * API service for landing page.
 * Makes authenticated calls to backend using JWT from localStorage.
 */

import axios from 'axios';
import { getAccessToken, clearTokens, refreshAccessToken } from './auth';
import { BASE_URL } from './config';

const API_URL = BASE_URL;

// Track if a refresh is in progress to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth header to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 → attempt token refresh, then redirect to frontend login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          isRefreshing = false;
          onTokenRefreshed(newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        isRefreshing = false;
        console.warn('Token refresh failed:', refreshError);
      }

      // Refresh failed - clear tokens and redirect to login
      clearTokens();
      const currentUrl = window.location.href;
      sessionStorage.setItem('redirectAfterLogin', currentUrl);
      window.location.href = `/login?redirect=${encodeURIComponent('/pricing')}`;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  type: 'monthly' | 'yearly';
  features?: string[];
  price_id?: string;
}

/**
 * Create a Stripe checkout session for the given plan.
 * Returns the Stripe checkout URL.
 */
export async function createCheckoutSession(priceId: string): Promise<string> {
  const { data } = await api.post<{ url: string }>('/api/v1/payments/create-checkout-session', {
    price_id: priceId,
  });
  return data.url;
}

/**
 * Get the current user's subscription info.
 * Returns null if not subscribed.
 */
export async function getSubscription(): Promise<SubscriptionPlan | null> {
  try {
    const { data } = await api.get<SubscriptionPlan>('/api/v1/payments/subscription');
    return data;
  } catch {
    return null;
  }
}

/**
 * Open the Stripe customer portal for subscription management.
 * Returns the portal URL.
 */
export async function openCustomerPortal(): Promise<string> {
  const { data } = await api.post<{ url: string }>('/api/v1/payments/portal');
  return data.url;
}

/**
 * Make a POST request with optional body.
 * Returns the response data directly.
 */
export async function ApiPost<T = unknown>(endpoint: string, body?: Record<string, unknown>): Promise<T> {
  const { data } = await api.post<T>(endpoint, body);
  return data;
}

/**
 * Make a GET request.
 * Returns the response data directly.
 */
export async function ApiGet<T = unknown>(endpoint: string): Promise<T> {
  const { data } = await api.get<T>(endpoint);
  return data;
}

/**
 * Log out from the landing page.
 * Clears local tokens. (No backend logout endpoint exists — Supabase handles session on client)
 */
export async function logoutFromLandingPage(): Promise<void> {
  clearTokens();
}